import React, { useState } from 'react';
import {
    DashboardOutlined,
    ClockCircleOutlined,
    ProjectOutlined,
    CheckSquareOutlined,
    FlagOutlined,
    TeamOutlined,
    VideoCameraOutlined,
    ThunderboltOutlined,
} from '@ant-design/icons';
import { ReportsProvider, useReports } from './context/ReportsContext';
import ReportsHeader from './components/ReportsHeader';
import ReportsFilterToolbar from './components/ReportsFilterToolbar';
import AiManagementSummaryCard from './components/AiManagementSummaryCard';
import ShareReportModal from './components/ShareReportModal';

import OverviewTab from './tabs/OverviewTab';
import DailyActivityTab from './tabs/DailyActivityTab';
import ProjectsTab from './tabs/ProjectsTab';
import TasksMilestonesTab from './tabs/TasksMilestonesTab';
import TeamWorkloadTab from './tabs/TeamWorkloadTab';
import MeetingsTab from './tabs/MeetingsTab';
import ForecastsTab from './tabs/ForecastsTab';

function ReportsContent({ employees = [], projects = [], onSelectTask, onSelectProject }) {
    const { activeTab, setActiveTab } = useReports();
    const [shareModalVisible, setShareModalVisible] = useState(false);
    const [visitedTabs, setVisitedTabs] = useState(() => new Set([activeTab || 'overview']));

    // Keep visited tabs in DOM (hidden) to prevent unmount refetches and preserve tab state
    React.useEffect(() => {
        setVisitedTabs(prev => {
            if (prev.has(activeTab)) return prev;
            const updated = new Set(prev);
            updated.add(activeTab);
            return updated;
        });
    }, [activeTab]);

    const navTabs = [
        { key: 'overview', label: 'Overview', icon: <DashboardOutlined /> },
        { key: 'daily_activity', label: 'Daily Activity', icon: <ClockCircleOutlined /> },
        { key: 'projects', label: 'Projects', icon: <ProjectOutlined /> },
        { key: 'tasks_milestones', label: 'Tasks & Milestones', icon: <CheckSquareOutlined /> },
        { key: 'team_workload', label: 'Team & Workload', icon: <TeamOutlined /> },
        { key: 'meetings', label: 'Meetings', icon: <VideoCameraOutlined /> },
        { key: 'forecasts', label: 'Forecasts', icon: <ThunderboltOutlined /> },
    ];

    return (
        <div className="w-full space-y-5">
            {/* Header with Share action & Refresh */}
            <ReportsHeader onOpenShare={() => setShareModalVisible(true)} />

            {/* Global Filters Bar */}
            <ReportsFilterToolbar employees={employees} projects={projects} />

            {/* AI Management Summary (accessible across tabs) */}
            <AiManagementSummaryCard />

            {/* Modern Pill Navigation Tabs (Matching Task Management & TaskViewTabs) */}
            <div className="flex bg-[#f3f4f5] dark:bg-black p-1.5 rounded-2xl overflow-x-auto gap-1 border border-slate-200/60 dark:border-neutral-800 shadow-2xs mb-5">
                {navTabs.map((tab) => {
                    const isActive = activeTab === tab.key;
                    return (
                        <button
                            key={tab.key}
                            type="button"
                            onClick={() => setActiveTab(tab.key)}
                            className={`
                                px-5 sm:px-6 py-2.5 rounded-xl text-sm font-semibold transition-all whitespace-nowrap flex items-center gap-2 cursor-pointer
                                ${isActive
                                    ? 'bg-white dark:bg-neutral-900 text-[#003263] dark:text-[#a7c8ff] shadow-sm font-bold'
                                    : 'text-[#424750] dark:text-slate-400 hover:text-[#003263] dark:hover:text-white'
                                }
                            `}
                        >
                            <span className={isActive ? 'text-[#00AAF1]' : 'text-slate-400'}>
                                {tab.icon}
                            </span>
                            <span>{tab.label}</span>
                        </button>
                    );
                })}
            </div>

            {/* Active Tab Workspace Content — cached & DOM-preserved for fast switching */}
            <div className="pt-1">
                {visitedTabs.has('overview') && (
                    <div className={activeTab === 'overview' ? 'block' : 'hidden'}>
                        <OverviewTab />
                    </div>
                )}
                {visitedTabs.has('daily_activity') && (
                    <div className={activeTab === 'daily_activity' ? 'block' : 'hidden'}>
                        <DailyActivityTab />
                    </div>
                )}
                {visitedTabs.has('projects') && (
                    <div className={activeTab === 'projects' ? 'block' : 'hidden'}>
                        <ProjectsTab onSelectProject={onSelectProject} />
                    </div>
                )}
                {visitedTabs.has('tasks_milestones') && (
                    <div className={activeTab === 'tasks_milestones' ? 'block' : 'hidden'}>
                        <TasksMilestonesTab onSelectTask={onSelectTask} onSelectMilestone={onSelectTask} />
                    </div>
                )}
                {visitedTabs.has('team_workload') && (
                    <div className={activeTab === 'team_workload' ? 'block' : 'hidden'}>
                        <TeamWorkloadTab />
                    </div>
                )}
                {visitedTabs.has('meetings') && (
                    <div className={activeTab === 'meetings' ? 'block' : 'hidden'}>
                        <MeetingsTab />
                    </div>
                )}
                {visitedTabs.has('forecasts') && (
                    <div className={activeTab === 'forecasts' ? 'block' : 'hidden'}>
                        <ForecastsTab />
                    </div>
                )}
            </div>

            {/* Share & Export Modal */}
            <ShareReportModal
                visible={shareModalVisible}
                onCancel={() => setShareModalVisible(false)}
            />
        </div>
    );
}

export default function ReportsWorkspace(props) {
    return (
        <ReportsProvider>
            <ReportsContent {...props} />
        </ReportsProvider>
    );
}
